import { defineStore, getActivePinia } from 'pinia'
import { type Logger, useLogger } from '@/composables/useLogger.ts'
import type { PiniaTitropExtended } from '@/helpers/piniaClearStorePlugin'
import { Router } from '@/helpers/router'

import { useBroadcastChannel } from '@vueuse/core'
import { startAuthentication, type StartAuthenticationOpts } from '@simplewebauthn/browser'
import {
  apiAuth2FaverifyPost,
  changePassword,
  type ChangePasswordResponse,
  type EmployeeJsonldDefaultReadSync,
  loginCheckPost,
  loginLogout,
  loginRefreshToken,
  type LoginRefreshTokenResponse,
  postWebauthLoginOptions,
  postWebauthLoginResult,
  requestResetPassword,
  resetPassword,
  WebauthLoginResultRequest,
  whoami
} from '@/api'
import { AxiosError } from 'axios'
import { doWithLock } from '@/helpers/locking'
import type { PublicKeyCredentialRequestOptionsJSON } from '@simplewebauthn/types'
import { watch } from 'vue'
import { useTitropEvents } from '@/composables/useTitropEvents.ts'
import { CookieSetOptions } from 'universal-cookie'
import { useCookies } from '@vueuse/integrations'

interface BroadcastMessage_RefreshDone {
  type: 'refresh_done'
  tokenData: LoginRefreshTokenResponse
}

interface BroadcastMessage_LoginSuccess {
  type: 'login_success'
  response: LoginResponse
}

interface BroadcastMessage_NoData {
  type: 'logout' | 'refresh_start'
}

type BroadcastMessage = BroadcastMessage_RefreshDone | BroadcastMessage_LoginSuccess | BroadcastMessage_NoData
const { post, data } = useBroadcastChannel<BroadcastMessage, BroadcastMessage>({ name: 'auth-synchronization' })

interface LoginResponse {
  refresh_token: string
  refresh_token_expiration: number
  token: string
}

interface TwoFactorResponse {
  tfa_required: true
  session_id?: string
}

type LoginApiResponse = LoginResponse | TwoFactorResponse

interface AuthStoreState {
  returnUrl?: string
  refresh_token?: string
  refresh_token_expiration?: number
  token?: string
  token_expiration?: number
  impersonated_user?: string
  whoami?: WhoAmI //not persisted
  requestInterceptorId?: number //not persisted
  responseInterceptorId?: number //not persisted
  refreshing: boolean

  tfaRequired: boolean // not persisted
  tfaSessionId?: string // not persisted
  pendingCredentials?: { username: string; password: string } // not persisted

  $logger: Logger
}

export type WhoAmI = EmployeeJsonldDefaultReadSync & {
  readonly id: number
  configuration?: Record<
      string,
      {
        id: number
        value: unknown
      }
  >
  roles?: string[]
  avatarUrl: Record<string, string>
}

function isTwoFactorResponse(response: LoginApiResponse): response is TwoFactorResponse {
  return 'tfa_required' in response && response.tfa_required === true
}

watch(data, () => {
  switch (data.value.type) {
    case 'logout':
      useAuthStore().$logger.info('Ein anderer Tab hat sich aktiv ausgeloggt.')
      void useAuthStore().logout(false)
      break
    case 'login_success':
      useAuthStore().$logger.info('Ein anderer Tab hat sich eingeloggt.')
      void useAuthStore().onLoginSuccessRedirect()
      break

    case 'refresh_start':
      useAuthStore().$logger.info('Ein anderer Tab hat den Refresh gestartet. Warte auf neues Token...')
      useAuthStore().refreshing = true
      break
    case 'refresh_done':
      useAuthStore().$logger.info('Neuer Token über BroadcastChannel erhalten.')
      useAuthStore().$storeResponse(data.value.tokenData)
      break
  }
})

const cookieOpts: CookieSetOptions = { path: '/', secure: true, sameSite: 'lax' }

export const useAuthStore = defineStore('auth', {
  state: (): AuthStoreState => ({
    returnUrl: undefined,
    refresh_token: undefined,
    refresh_token_expiration: undefined,
    token: undefined,
    token_expiration: undefined,
    impersonated_user: undefined,
    whoami: undefined, //not persisted
    requestInterceptorId: undefined, //not persisted
    responseInterceptorId: undefined, //not persisted
    $logger: useLogger('AuthStore'), //not persisted

    refreshing: false, // Multi-Tab Synchronisation //not persisted


    tfaRequired: false, // not persisted
    tfaSessionId: undefined, // not persisted
    pendingCredentials: undefined // not persisted
  }),
  getters: {
    isLoggedIn(state): boolean {
      return !!state.whoami
    },

    requiresTwoFactor(state): boolean {
      return state.tfaRequired
    }
  },
  actions: {
    clearTwoFactorState() {
      this.tfaRequired = false
      this.tfaSessionId = undefined
      this.pendingCredentials = undefined
    },

    isImpersonated(): boolean {
      return this.impersonated_user !== undefined
    },

    async startUserImpersonation(userIdentifier: string) {
      console.log(`Starting impersonation for ${userIdentifier}`)
      this.impersonated_user = userIdentifier
      await this.$postImpersonationChange()
    },

    async stopUserImpersonation() {
      console.log('Stopping user impersonation')
      this.impersonated_user = undefined
      await this.$postImpersonationChange()
    },

    async $postImpersonationChange() {
      ;(getActivePinia() as PiniaTitropExtended).$clearCaches()
      this.$logger.debug('Getting whoami')
      await this.refreshWhoAmI()
    },

    async recoverPassword(
        verificationCode: string,
        passwort: string
    ): Promise<{
      result: 'success' | 'password_unsafe' | 'invalid_params' | 'error'
      error?: unknown
    }> {
      try {
        await resetPassword<true>({
          body: { password: passwort, verification_code: verificationCode }
        })
        return { result: 'success', error: undefined }
      } catch (e) {
        return { result: 'invalid_params', error: e }
      }
    },

    async requestNewPasswort(username: string): Promise<{
      result: boolean
      error?: unknown
    }> {
      try {
        await requestResetPassword({
          body: {
            username: username,
            applicationUrl: location.origin
          }
        })
        return { result: true, error: undefined }
      } catch (e) {
        return { result: false, error: e }
      }
    },

    async changePassword(currentPassword: string, newPassword: string): Promise<ChangePasswordResponse> {
      try {
        const response = await changePassword({
          body: {
            new: newPassword,
            current: currentPassword
          }
        })

        if (response.error) throw response
        return response.data
      } catch (e: unknown) {
        if (e instanceof AxiosError && e.response?.data) {
          return e.response.data
        } else
          return {
            success: false,
            errors: { current: String(e) }
          }
      }
    },

    async checkIfAlreadyLoggedIn() {
      if (this.token || (this.refresh_token && this.refresh_token_expiration && new Date(this.refresh_token_expiration * 1000) <= new Date())) {
        try {
          await this.refreshLoginState(false)
          return true
        } catch {
          /* empty */
        }
      }
      return false
    },

    async refreshWhoAmI() {
      this.whoami = (await whoami()).data as WhoAmI
      if (!this.whoami.roles) {
        debugger
      }
      const titrop = useTitrop()
      return (titrop.value.user.current = this.whoami)
    },

    async refreshLoginState(autologoutOnFail = true) {
      if (this.isLoggedIn) return //already checked
      if (!this.token) return //there is nothing to check
      this.$logger.debug('Refreshing login state')

      this.$logger.debug('Getting whoami')
      await this.refreshWhoAmI()
      try {
        useTitropEvents().onLoginSuccess.emit(this.whoami)
      } catch (e) {
        console.error(e)
        if (autologoutOnFail) await this.logout()
        throw e
      }
    },

    async refreshToken(): Promise<string | false> {
      return doWithLock('auth_refreshToken', async () => {
        if (!this.refresh_token || !this.refresh_token_expiration) return false
        if (new Date().getTime() >= this.refresh_token_expiration * 1000 - 60 * 1000) return false

        if (this.refreshing) {
          this.$logger.debug('Ein anderer Tab erneuert bereits den Token, warte...')

          return new Promise((resolve) => {
            const stopWatch = watch(data, (newData) => {
              if (newData.type === 'refresh_done') {
                this.$logger.debug('Neuer Token empfangen, übernehme...')
                this.$storeResponse(newData.tokenData)
                stopWatch()
                resolve(newData.tokenData.token)
              }
            })
          })
        }

        this.refreshing = true
        post({ type: 'refresh_start' })

        this.$logger.debug('Starte Token-Refresh...')
        try {
          const response: LoginRefreshTokenResponse = await loginRefreshToken<true>({
            body: { refresh_token: this.refresh_token }
          }).then((d) => d.data)
          this.$storeResponse(response)

          post({ type: 'refresh_done', tokenData: response })
          this.$logger.debug('Token erfolgreich aktualisiert.')
          useTitropEvents().onTokenRefreshed.emit()
          await this.refreshLoginState()
          return response.token
        } catch (_e) {
          this.$logger.warn('Token-Refresh fehlgeschlagen.')
          return false
        } finally {
          this.refreshing = false
        }
      })
    },

    async onLoginSuccessRedirect() {
      const c = (err: Error) => {
        console.error('Routing-Fehler:', err)
      }

      if (!this.returnUrl || this.returnUrl.startsWith('/login')) {
        await Router.push('/').catch(c)
      } else {
        await Router.push(this.returnUrl).catch(c)
      }
    },

    async handleLoginSuccess(response: LoginResponse, redirect = true) {
      this.clearTwoFactorState()
      this.$storeResponse(response)
      post({ type: 'login_success', response: response })
      this.$logger.debug('Login SUCCESS')
      await this.refreshLoginState(false)
      if (redirect) await this.onLoginSuccessRedirect()
    },

    async handleTwoFactorRequired(response: TwoFactorResponse, username: string, password: string) {
      this.$logger.debug('2FA required')
      this.tfaRequired = true
      this.tfaSessionId = response.session_id
      this.pendingCredentials = { username, password }
    },

    async completeTwoFactor(totpCode: string): Promise<{ success: boolean; error?: string }> {
      if (!this.tfaRequired || !this.pendingCredentials) {
        return { success: false, error: 'No pending 2FA authentication' }
      }

      return doWithLock('auth_complete_2fa', async () => {
        try {
          const response = await apiAuth2FaverifyPost({
            body: {
              totp_code: totpCode,
              session_id: this.tfaSessionId
            }
          })

          await this.handleLoginSuccess(response.data)
          return { success: true }
        } catch (e) {
          this.$logger.debug('2FA completion FAILED')
          if (e instanceof AxiosError && e.response?.status === 401) {
            return { success: false, error: 'Invalid 2FA code' }
          }
          return { success: false, error: 'Authentication failed' }
        }
      })
    },

    async loginWebauthn() {
      const cb = async () => {
        this.$logger.debug('Starting passkey ceremony')
        try {
          const authJsonOpts: PublicKeyCredentialRequestOptionsJSON = (
              await postWebauthLoginOptions({
                headers: {
                  'Content-Type': 'application/json',
                  Accept: 'application/json'
                },
                body: {}
              })
          ).data as unknown as PublicKeyCredentialRequestOptionsJSON

          const authOpts: StartAuthenticationOpts = {
            optionsJSON: authJsonOpts,
            useBrowserAutofill: false,
            verifyBrowserAutofillInput: false
          }

          const res: WebauthLoginResultRequest = (await startAuthentication(authOpts)) as WebauthLoginResultRequest
          const authResult = (
              await postWebauthLoginResult<true>({
                headers: {
                  'Content-Type': 'application/json',
                  Accept: 'application/json'
                },
                body: res
              })
          ).data

          if (authResult.status == 'ok') {
            const res2 = authResult as unknown as LoginResponse
            if (!res2.token) {
              this.$logger.error('WebAuthn login success but server returned no token!')
            }
            await this.handleLoginSuccess(res2)
            return
          } else {
            this.$logger.debug('Login FAILED')
            this.$logger.debug(JSON.stringify(authResult))
          }
        } catch (e) {
          this.$logger.debug('Login FAILED')
          throw e
        }

        throw new Error('Webauth status not ok')
      }
      return doWithLock('auth_login', cb)
    },

    async login(username: string, password: string): Promise<{ requiresTwoFactor: boolean }> {
      return doWithLock('auth_login', async () => {
        this.$logger.debug('Logging in')

        try {
          const response: LoginApiResponse = await loginCheckPost<true>({
            body: {
              username: username,
              password: password
            }
          }).then((d) => d.data as LoginApiResponse)

          if (isTwoFactorResponse(response)) {
            await this.handleTwoFactorRequired(response, username, password)
            return { requiresTwoFactor: true }
          } else {
            await this.handleLoginSuccess(response)
            return { requiresTwoFactor: false }
          }
        } catch (e) {
          this.$logger.debug('Login FAILED')
          this.clearTwoFactorState()
          throw e
        }
      })
    },

    async logout(broadcast = true) {
      this.$logger.debug('Logging OUT')
      this.clearTwoFactorState() // Очистка состояния 2FA при логауте

      if (broadcast) post({ type: 'logout' })
      await loginLogout()
      ;(getActivePinia() as PiniaTitropExtended).$resetAll()
      useCookies().remove('access_token', cookieOpts)

      useTitropEvents().onLogout.emit()
      if (Router.currentRoute.value.name != 'login') location.replace('/')
    },

    $storeResponse(response: {
      readonly token: string;
      readonly refresh_token: string;
      readonly refresh_token_expiration: number;
      readonly token_expiration?: number;
    }) {
      this.$logger.debug('Storing response: ', response)
      this.token = response.token
      this.token_expiration = response.token_expiration
      useCookies().set('access_token', this.token, {
        ...cookieOpts,
        expires: response.token_expiration ? new Date(response.token_expiration * 1000) : undefined
      })
      this.refresh_token = response.refresh_token
      this.refresh_token_expiration = response.refresh_token_expiration
    }
  },
  persist: {
    pick: ['returnUrl', 'refresh_token', 'refresh_token_expiration', 'token', 'token_expiration', 'impersonated_user'],
    afterHydrate: (ctx) => {
      if (!ctx.store.token || !ctx.store.token_expiration) {
        useCookies().remove('access_token', cookieOpts)
      } else {
        useCookies().set('access_token', ctx.store.token, {
          ...cookieOpts,
          expires: new Date(ctx.store.token_expiration * 1000)
        })
      }
    }
  }
})
